import type { z } from 'zod';

import type { Device } from '../../utils';
import { CORNERS, atDevice } from '../../utils';
import { createVersionedFeature } from '../create-versioned-feature';
import { borderRadiusV0Schema } from './v0';

const everywhere = <T>(make: () => T) => ({ desktop: make(), tablet: make(), mobile: make() });

/**
 * The rounding of a box's corners.
 *
 * Only the **column** rounds itself today — a block spans the page and a section
 * is laid out inside one — but the corners are the likeliest of these four to
 * grow (a per-corner unit, an elliptical radius), which is what a self-versioned
 * feature is for: it can change shape without bumping the column's version.
 */
export const borderRadiusFeature = createVersionedFeature({
    versionMap: {
        0: { schema: borderRadiusV0Schema },
    },
    latestVersion: 0,
    createDefault: (): z.output<typeof borderRadiusV0Schema> => ({
        version: 0,
        corners: everywhere(() => ({
            value: { topLeft: 0, topRight: 0, bottomRight: 0, bottomLeft: 0 },
            unit: 'px' as const,
        })),
    }),
});

export type BorderRadiusSettings = z.output<typeof borderRadiusFeature.latestSchema>;
export type BorderRadiusCorners = BorderRadiusSettings['corners']['desktop']['value'];

/**
 * Whether the corners are rounded one by one at `device` — the answer legacy kept
 * in `isIndependentCorners`, computed from the corners instead of stored beside
 * them.
 *
 * The unit is shared by the whole leaf, so only the four numbers can disagree.
 * Nothing about the drawing depends on this: the CSS emits all four corners
 * either way. It only decides whether the editor shows one control or four.
 */
export const isBorderRadiusIndependent = (
    radius: Pick<BorderRadiusSettings, 'corners'> | undefined,
    device: Device = 'desktop'
): boolean => {
    const corners = atDevice(radius?.corners, device)?.value;

    if (!corners) {
        return false;
    }

    return !CORNERS.every((corner) => corners[corner] === corners.topLeft);
};

export { borderRadiusV0Schema };

export { borderRadiusCornerVar, borderRadiusVars } from './border-radius.css-vars';
